import axios from "axios";
import Swal from 'sweetalert2';

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 5000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener('mouseenter', Swal.stopTimer);
    toast.addEventListener('mouseleave', Swal.resumeTimer);
  }
});

const SET_SELECTED_LOCATION = 'SET_SELECTED_LOCATION';
const SET_GBP_CATEGORIES = 'SET_GBP_CATEGORIES';
const SET_LOCATION = 'SET_LOCATION';
const SET_CONNECTED_LOCATIONS = 'SET_CONNECTED_LOCATIONS';
const SET_TITLE = 'SET_TITLE';
const SET_ADDRESS_LINES = 'SET_ADDRESS_LINES';
const ADD_ADDRESS_LINE = 'ADD_ADDRESS_LINE';
const SET_ADDRESS_LINES_BY_INDEX = 'SET_ADDRESS_LINES_BY_INDEX';
const SET_LOCALITY = 'SET_LOCALITY';
const SET_POSTAL_CODE = 'SET_POSTAL_CODE';
const SET_PRIMARY_PHONE = 'SET_PRIMARY_PHONE';
const SET_ADDITIONAL_PHONES = 'SET_ADDITIONAL_PHONES';
const SET_ADDITIONAL_PHONES_BY_INDEX = 'SET_ADDITIONAL_PHONES_BY_INDEX';
const SET_PRIMARY_CATEGORY = 'SET_PRIMARY_CATEGORY';
const SET_ADDITIONAL_CATEGORIES = 'SET_ADDITIONAL_CATEGORIES';
const SET_DESCRIPTION = 'SET_DESCRIPTION';
const SET_PERIODS = 'SET_PERIODS';
const SET_WEBSITE_URI = 'SET_WEBSITE_URI';
const SET_VALIDATION_ERRORS = 'SET_VALIDATION_ERRORS';
const SET_LOCATION_LOADING = 'SET_LOCATION_LOADING';

const state = {
  selectedLocation: null,
  location: {},
  locationCompare: '',
  updated: false,
  connectedLocations: [],
  gbpCategories: [],
  validationErrors: [],
  locationLoading: false,
};

function compareObjectsAsStrings(a, b) {
  return JSON.stringify(a) !== JSON.stringify(b);
}

const getters = {
  updated(state) {
    return state.updated;
  },
  selectedLocation(state) {
    return state.selectedLocation;
  },
  location(state) {
    return state.location;
  },
  connectedLocations(state) {
    return state.connectedLocations;
  },
  gbpCategories(state) {
    return state.gbpCategories;
  },
  hasStorefrontAddress(state) {
    return (state.location?.storefrontAddress);
  },
  addressLines(state) {
    return state.location?.storefrontAddress?.addressLines ?? [];
  },
  locality(state) {
    return state.location?.storefrontAddress?.locality ?? '';
  },
  postalCode(state) {
    return state.location?.storefrontAddress?.postalCode ?? '';
  },
  title(state) {
    return state.location?.title ?? '';
  },
  primaryPhone(state) {
    return state.location?.phoneNumbers?.primaryPhone;
  },
  additionalPhones(state) {
    return state.location?.phoneNumbers?.additionalPhones;
  },
  primaryCategory(state) {
    return state.location?.categories?.primaryCategory ?? {};
  },
  additionalCategories(state) {
    return state.location?.categories?.additionalCategories ?? {};
  },
  description(state) {
    return state.location?.profile?.description ?? '';
  },
  periods(state) {
    return state.location?.regularHours?.periods ?? [];
  },
  websiteUri(state) {
    return state.location?.websiteUri ?? '';
  },
  regionCode(state) {
    return state.location?.storefrontAddress?.regionCode ?? '';
  },
  validationErrors(state) {
    return state.validationErrors;
  },
  locationLoading(state) {
    return state.locationLoading;
  },
  serviceAreaBusinessType(state) {
    return state.location?.serviceArea?.businessType ?? null;
  }
};

const actions = {
  updateSelectedLocation({ commit, dispatch }, selectedLocation) {
    return new Promise(resolve => {
      window.history.pushState(
        {
          type: 'vue-nav',
          id: selectedLocation
        },
        document.title,
        `/local/business-profile/${selectedLocation}`
      );
      window.onpopstate = function(e){
        if(e.state && e.state.type === 'vue-nav'){
          commit(SET_SELECTED_LOCATION, e.state.id);
        }
      };
      commit(SET_SELECTED_LOCATION, selectedLocation);
      dispatch('fetchConnectedLocations');
      resolve();
    });
  },
  fetchLocation({ commit }, id) {
    return axios.get(`/gbp/get-location/${id}`)
      .then(({ data }) => {
        commit(SET_LOCATION, data.location);
      })
      .catch(({ response }) => {
        Toast.fire({
          icon: 'error',
          title: response.data.message
        });
        commit(SET_LOCATION, response.data.location);
      });
  },
  clearLocation({ commit }) {
    commit(SET_LOCATION, null);
  },
  fetchGbpCategories({ commit, getters }) {
    axios.get(`/gbp/get-categories/${getters.selectedLocation}?region=${getters.regionCode}`)
      .then(({ data }) => {
        commit(SET_GBP_CATEGORIES, data);
      });
  },
  fetchConnectedLocations({ commit }) {
    axios.get('/gbp/get-connected-locations')
      .then(({ data }) => {
        commit(SET_CONNECTED_LOCATIONS, data.connectedLocations);
      });
  },
  updateTitle({ commit }, title) {
    commit(SET_TITLE, title);
  },
  addAddressLine({ commit }) {
    commit(ADD_ADDRESS_LINE);
  },
  updateAddressLineByIndex({ commit }, payload) {
    commit(SET_ADDRESS_LINES_BY_INDEX, payload);
  },
  updateLocality({ commit }, value) {
    commit(SET_LOCALITY, value);
  },
  updatePostalCode({ commit }, value) {
    commit(SET_POSTAL_CODE, value);
  },
  updatePrimaryPhone({ commit }, value) {
    commit(SET_PRIMARY_PHONE, value);
  },
  updateAdditionalPhoneByIndex({ commit }, payload) {
    commit(SET_ADDITIONAL_PHONES_BY_INDEX, payload);
  },
  addAdditionalPhone({ commit }, currentAdditionalPhones) {
    if (currentAdditionalPhones && currentAdditionalPhones.length > 0) {
      currentAdditionalPhones.push('');
      console.log(currentAdditionalPhones);
      commit(SET_ADDITIONAL_PHONES, currentAdditionalPhones);
      return;
    }
    commit(SET_ADDITIONAL_PHONES, ['']);
  },
  updatePrimaryCategory({ commit }, value) {
    commit(SET_PRIMARY_CATEGORY, value);
  },
  updateAdditionalCategoriesFromArray({ commit }, categoriesArray) {
    let newAdditionalCategories = [];
    _.each(categoriesArray, (category) => {
      const categoryObject = {
        'name': category
      };
      newAdditionalCategories.push(categoryObject);
    });
    commit(SET_ADDITIONAL_CATEGORIES, newAdditionalCategories);
  },
  updateDescription({ commit }, value) {
    commit(SET_DESCRIPTION, value);
  },
  updatePeriods({ commit }, value) {
    commit(SET_PERIODS, value);
  },
  updateWebsiteUri({ commit }, value) {
    commit(SET_WEBSITE_URI, value);
  },
  saveLocationChanges({ commit, dispatch }, location) {
    return new Promise((resolve, reject) => {
      commit(SET_LOCATION_LOADING, true);
      axios.post(`/gbp/update-location/${location.id}`, {
        location
      }).then(({ data }) => {
        dispatch('fetchLocation', location.id);
        commit(SET_LOCATION_LOADING, false);
        Toast.fire({
          icon: 'success',
          title: `Location updated successfully`
        });
        resolve();
      }).catch(({ response }) => {
        commit(SET_VALIDATION_ERRORS, response.data.errors);
        commit(SET_LOCATION_LOADING, false);
        Toast.fire({
          icon: 'error',
          title: response.data.message
        });
        reject(response);
      });
    });
  }
};

const mutations = {
  [SET_SELECTED_LOCATION](state, selectedLocation) {
    state.selectedLocation = selectedLocation;
  },
  [SET_GBP_CATEGORIES](state, categories) {
    state.gbpCategories = categories;
  },
  [SET_LOCATION](state, location) {
    state.location = location;
    state.locationCompare = _.cloneDeep(location);
    state.updated = false;
  },
  [SET_CONNECTED_LOCATIONS](state, locations){
    state.connectedLocations = locations;
  },
  [SET_TITLE](state, title) {
    state.location.title = title;
    state.updated = compareObjectsAsStrings(state.location, state.locationCompare);
    delete state.validationErrors.title;
  },
  [SET_ADDRESS_LINES](state, value) {
    if (!state.location.storefrontAddress) {
      state.location.storefrontAddress = {};
    }
    state.location.storefrontAddress.addressLines = value;
    state.updated = compareObjectsAsStrings(state.location, state.locationCompare);
  },
  [SET_ADDRESS_LINES_BY_INDEX](state, { value, index }) {
    if (!value && index !== 0){
      state.location.storefrontAddress.addressLines.splice(index, 1);
      return;
    }
    state.location.storefrontAddress.addressLines[index] = value;
    state.updated = compareObjectsAsStrings(state.location, state.locationCompare);
  },
  [ADD_ADDRESS_LINE](state, value) {
    state.location.storefrontAddress.addressLines.push('');
    state.updated = compareObjectsAsStrings(state.location, state.locationCompare);
  },
  [SET_LOCALITY](state, value) {
    state.location.storefrontAddress.locality = value;
    state.updated = compareObjectsAsStrings(state.location, state.locationCompare);
  },
  [SET_POSTAL_CODE](state, value) {
    state.location.storefrontAddress.postalCode = value;
    state.updated = compareObjectsAsStrings(state.location, state.locationCompare);
  },
  [SET_PRIMARY_PHONE](state, value) {
    if (!state.location.phoneNumbers) {
      state.location.phoneNumbers = {};
    }
    state.location.phoneNumbers.primaryPhone = value;
    state.updated = compareObjectsAsStrings(state.location, state.locationCompare);
  },
  [SET_ADDITIONAL_PHONES](state, value) {
    state.location.phoneNumbers.additionalPhones = value;
    state.updated = compareObjectsAsStrings(state.location, state.locationCompare);
  },
  [SET_ADDITIONAL_PHONES_BY_INDEX](state, {value, index}) {
    if (!value){
      state.location.phoneNumbers.additionalPhones.splice(index, 1);
      if (!state.location.phoneNumbers.additionalPhones.length) {
        state.location.phoneNumbers.additionalPhones = null;
      }
    } else {
      state.location.phoneNumbers.additionalPhones[index] = value;
    }
    state.updated = compareObjectsAsStrings(state.location, state.locationCompare);
  },
  [SET_PRIMARY_CATEGORY](state, value) {
    state.location.categories.primaryCategory = value;
    state.updated = compareObjectsAsStrings(state.location, state.locationCompare);
  },
  [SET_ADDITIONAL_CATEGORIES](state, value) {
    state.location.categories.additionalCategories = value;
    state.updated = compareObjectsAsStrings(state.location, state.locationCompare);
  },
  [SET_DESCRIPTION](state, value) {
    if (!state.location.profile) {
      state.location.profile = {};
    }
    if (!value && !state.locationCompare.profile) {
      state.location.profile = null;
    } else {
      state.location.profile.description = value;
    }
    state.updated = compareObjectsAsStrings(state.location, state.locationCompare);
    delete state.validationErrors['profile.description'];
  },
  [SET_PERIODS](state, value) {
    if (!state.location.regularHours) {
      state.location.regularHours = {};
    }
    state.location.regularHours.periods = value;
    state.updated = compareObjectsAsStrings(state.location, state.locationCompare);
  },
  [SET_WEBSITE_URI](state, value) {
    state.location.websiteUri = value;
    state.updated = compareObjectsAsStrings(state.location, state.locationCompare);
  },
  [SET_VALIDATION_ERRORS](state, errors) {
    state.validationErrors = errors ?? [];
  },
  [SET_LOCATION_LOADING](state, bool) {
    state.locationLoading = bool;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
